import { useState } from 'react';
import type { ConnectorProductItem, ConnectorResultCardMetadata } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { useTranslation } from '../../../../lib/i18n';
import { ENTITY_TYPE } from '../../../../lib/use-generic-bi';
import { useItemSeenBi } from '../../../../lib/use-item-seen-bi';
import { ShopifySourceLine } from '../../connectors/ShopifySourceLine';
import { ConnectorLogo } from '../../connectors/ConnectorLogo';
import { Badge, Card, Typography } from '../../ui';
import styles from './ConnectorResultCard.module.css';

function formatPrice(price: NonNullable<ConnectorProductItem['price']>, locale: string): string {
  try {
    const format = new Intl.NumberFormat(locale, { style: 'currency', currency: price.currencyCode });
    const minimum = format.format(Number(price.amount));
    const hasRange = price.maxAmount !== undefined && Number(price.maxAmount) !== Number(price.amount);
    return hasRange ? `${minimum} – ${format.format(Number(price.maxAmount))}` : minimum;
  } catch {
    // A currency code Intl doesn't know still shows its amount.
    return `${price.amount} ${price.currencyCode}`;
  }
}

export function ConnectorProductRow({ item }: { item: ConnectorProductItem }) {
  const { t, i18n } = useTranslation();
  const [failedImage, setFailedImage] = useState<string>();
  const showImage = item.imageUrl !== undefined && item.imageUrl !== failedImage;
  // A ready image that failed to load reads as unavailable, not as ready.
  const imageLabel = failedImage || item.imageState === 'ready' ? 'unknown' : item.imageState;

  return (
    <div className={styles.product}>
      <div className={styles.image}>
        {showImage ? (
          <img src={item.imageUrl} alt={item.imageAlt ?? item.title} loading="lazy" referrerPolicy="no-referrer" onError={() => setFailedImage(item.imageUrl)} />
        ) : (
          <span className={styles.imageLabel}>{t(`chat.connectorResult.image.${imageLabel}`)}</span>
        )}
      </div>
      <div className={styles.details}>
        <div className={styles.heading}>
          <Typography variant="h4">{item.title}</Typography>
          {item.status && <Badge label={t(`chat.connectorResult.status.${item.status}`)} />}
        </div>
        {item.price && <div className={styles.price}>{formatPrice(item.price, i18n.language)}</div>}
        <div className={styles.facts}>
          {item.variantsCount !== undefined && <span>{t('chat.connectorResult.variants', { count: item.variantsCount })}</span>}
          {item.inventory !== undefined && <span>{t('chat.connectorResult.inventory', { count: item.inventory })}</span>}
        </div>
        {item.description && <p className={styles.description}>{item.description}</p>}
      </div>
    </div>
  );
}

/** The Shopify product answer under an assistant reply that carries `metadata.connectorResultCard`. Read-only: no controls. */
export function ConnectorResultCard({ card, conversationId }: { card: ConnectorResultCardMetadata; conversationId?: string }) {
  const { t } = useTranslation();
  const seenRef = useItemSeenBi('connector_result_card', {
    ...(conversationId ? { entity: { id: conversationId, type: ENTITY_TYPE.conversation } } : {}),
    additionalFields: { provider: card.provider, count: String(card.items.length) },
  });

  return (
    <Card component="section" ref={seenRef} className={styles.card} data-testid="connector-result-card">
      <header className={styles.header}>
        <ConnectorLogo connector={{ toolkit: 'shopify', connected: true }} name="Shopify" size={28} glyphSize={18} radius={8} />
        <Typography variant="h4">{t('contacts.channel.shopify')}</Typography>
        <span className={styles.count}>{t('chat.connectorResult.products', { count: card.items.length })}</span>
      </header>
      <ul className={styles.items}>
        {card.items.map((item) => (
          <li key={item.id}>
            <ConnectorProductRow item={item} />
          </li>
        ))}
      </ul>
      {card.source && (
        <div className={styles.source}>
          <ShopifySourceLine source={card.source} />
        </div>
      )}
    </Card>
  );
}
